import CopyIcon from '@mui/icons-material/ContentCopy';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import useResponsive from '@/hooks/useResponsive';
import {
  getBgStatus,
  getBgStatusFF,
  getLbColorStatus,
  getLbColorStatusFF,
  getStatusLabel,
  pickupStatusColor,
} from '@/utils/orders';

import PairTitle from './PairTitle';

interface OrderData {
  createdAt?: string | Date;
  trackingId?: string;
  internalKey?: string;
  deliveryType?: string;
  size?: string;
  status?: string;
  statusNameES?: string;
}

interface OrderInfoProps {
  orderData?: OrderData;
  isFFOrder?: boolean;
  isPickups?: boolean;
  copyCode: HTMLSpanElement | null;
  popoverOpen: boolean;
  idCopyCode: string | undefined;
  copy: (event: React.MouseEvent<HTMLSpanElement>, text: string) => void;
}

const OrderInfo = ({
  orderData,
  isFFOrder,
  isPickups,
  copyCode,
  popoverOpen,
  idCopyCode,
  copy,
}: OrderInfoProps) => {
  const isTablet = useResponsive('up', 'lg');
  const theme = useTheme();
  const { t } = useTranslation('commonGeneral');

  const orderInfoItems = [
    {
      title: 'Fecha de creación',
      value: orderData?.createdAt
        ? format(new Date(orderData.createdAt), 'dd/MM/yyyy')
        : format(new Date(), 'dd/MM/yyyy'),
    },
    {
      title: 'Nº Seguimiento',
      value: orderData?.trackingId ?? '',
      isBold: true,
      isGreen: true,
    },
    {
      title: 'ID Interno',
      value: orderData?.internalKey ?? '',
      icon: (
        <Box>
          {isTablet ? (
            <IconButton
              onClick={(event: React.MouseEvent<HTMLSpanElement>) =>
                copy(event, orderData?.internalKey ?? '')
              }
              sx={{
                color: theme.palette.primary.main,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <CopyIcon />
            </IconButton>
          ) : (
            <Link
              to={`tel:${orderData?.internalKey}`}
              style={{ color: theme.palette.primary.main }}
            >
              <CopyIcon sx={{ cursor: 'pointer' }} />
            </Link>
          )}
          <Popover
            id={idCopyCode}
            open={popoverOpen}
            anchorEl={copyCode}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
          >
            <Typography p={1} variant="body2" color={theme.palette.secondary.main}>
              ¡Copiado!
            </Typography>
          </Popover>
        </Box>
      ),
    },
    {
      title: 'Modalidad',
      value: t(`deliveryType.${orderData?.deliveryType ?? ''}`),
    },
    {
      title: 'Tamaño',
      children: (
        <StatusChip
          status={orderData?.size ?? ''}
          bg={`${theme.palette.primary.light}`}
          color={`${theme.palette.primary.main}`}
        />
      ),
    },
    {
      title: 'Estatus:',
      children: isPickups ? (
        <StatusChip
          status={orderData?.statusNameES ?? ''}
          color={pickupStatusColor(orderData?.statusNameES ?? '').bg}
          bg={pickupStatusColor(orderData?.statusNameES ?? '').color}
        />
      ) : (
        <StatusChip
          status={
            isFFOrder ? getStatusLabel(orderData?.status ?? '') : (orderData?.statusNameES ?? '')
          }
          color={
            isPickups
              ? pickupStatusColor(orderData?.statusNameES ?? '').bg
              : isFFOrder
                ? getLbColorStatusFF(orderData?.status ?? '')
                : getLbColorStatus(orderData?.status ?? '')
          }
          bg={
            isPickups
              ? pickupStatusColor(orderData?.statusNameES ?? '').color
              : isFFOrder
                ? getBgStatusFF(orderData?.status ?? '')
                : getBgStatus(orderData?.status ?? '')
          }
        />
      ),
    },
  ];

  return (
    <Grid container spacing={2} width={1}>
      {orderInfoItems.map((item, index) => (
        <Grid key={index} size={{ xxs: 12, md: 4 }}>
          <PairTitle {...item} />
        </Grid>
      ))}
    </Grid>
  );
};

export default OrderInfo;
